import { uploadData } from "aws-amplify/storage";
import { useCallback, useMemo, useReducer } from "react";
import { v4 as uuidv4 } from "uuid";

import type { ItemWithPath } from "@aws-amplify/storage/dist/esm/providers/s3/types/outputs";
import useAuth from "../common/unhealthie/hooks/useAuth";
import toast from "react-hot-toast";
import {
  CHIME_MAX_ATTRIBUTE_SIZE,
  MAX_FILENAME_LENGTH,
  estimateAttachmentAttributeSize,
  MAX_EXTENSION_LENGTH,
  useChat,
} from "../common/unhealthie/chat/providers/ChimeChatProvider";
import { useSelectedChatChannel } from "../common/unhealthie/chat/providers/ChatProvider/SelectedChannel";
import { useChatImplementation } from "../common/unhealthie/chat/providers/ChatProvider/useChatMigration";
import { useIdentity } from "./IdentityProvider";
import { fireClientPortalMixpanelEvent } from "../common/unhealthie/hooks/useMixpanel";

export const FileTypes = {
  IMAGE: "image/*",
  PDF: "application/pdf",
};

export type FileStatus = {
  fileObjectUrl: string;
  fileName: string;
  fileType: string;
  fileLastModified: number;
  fileUploadError: string | null;
  fileUploadIsLoading: boolean;
  fileUploadData: ItemWithPath | null;
  // Null when no upload in progress, otherwise a number between 0 and 100.
  fileUploadProgress: number | null;
};

type FileKey = string;

export type FileUploadStore = Record<FileKey, FileStatus>;

export type FileUploadUpdateAction =
  | ({
      key: FileKey;
    } & (
      | { type: "start"; file: File }
      | { type: "remove" }
      | { type: "progress"; fileUploadProgress: number }
      | { type: "finish"; fileUploadData: ItemWithPath }
      | { type: "error"; fileUploadError: string }
    ))
  | { type: "restore"; fileStore: FileUploadStore }
  | { type: "clear" };

export const fileStoreReducer = (
  state: FileUploadStore,
  action: FileUploadUpdateAction,
) => {
  switch (action.type) {
    case "start":
      const fileName = truncateFileName(action.file.name);
      const fileType = action.file.type;
      // If a record with the same fileName exists in state, show a message
      // and exit early.
      if (
        Object.values(state).some(
          (fileStatus) =>
            fileStatus.fileName === fileName &&
            fileStatus.fileLastModified === action.file.lastModified,
        )
      ) {
        return state;
      }

      return {
        ...state,
        [action.key]: {
          fileUploadIsLoading: true,
          fileName,
          fileType,
          fileLastModified: action.file.lastModified,
          fileObjectUrl: URL.createObjectURL(action.file),
          fileUploadData: null,
          fileUploadError: null,
          fileUploadProgress: null,
        },
      };
    case "progress":
      // If action.key isn't in state, ignore and exit early.
      if (!state[action.key]) {
        return state;
      }

      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          fileUploadIsLoading: true,
          fileUploadProgress: action.fileUploadProgress,
        },
      };
    case "finish":
      // If action.key isn't in state, ignore and exit early.
      if (!state[action.key]) {
        return state;
      }

      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          fileUploadIsLoading: false,
          fileUploadData: action.fileUploadData,
          fileUploadProgress: null,
        },
      };
    case "error":
      // If action.key isn't in state, ignore and exit early.
      if (!state[action.key]) {
        return state;
      }

      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          fileUploadIsLoading: false,
          fileUploadData: null,
          fileUploadError: action.fileUploadError.toString(),
          fileUploadProgress: null,
        },
      };
    case "clear":
      return {};
    case "remove":
      // `rest` is the same as `state`, but without the given key.
      const { [action.key]: _, ...rest } = state;
      return rest;
    case "restore":
      return action.fileStore;
    default:
      throw new Error("Unknown action type");
  }
};

export const useFileUpload = () => {
  const [fileStore, updateFileStore] = useReducer(fileStoreReducer, {});

  const uploadFile = useCallback(async (file: File, objectKey: string) => {
    updateFileStore({
      key: objectKey,
      type: "start",
      file,
    });

    const result = uploadData({
      path: objectKey,
      data: await file.arrayBuffer(),
      options: {
        contentType: file.type,
        onProgress: ({ transferredBytes, totalBytes }) => {
          if (totalBytes) {
            updateFileStore({
              key: objectKey,
              type: "progress",
              fileUploadProgress: Math.round(
                (transferredBytes / totalBytes) * 100,
              ),
            });
          } else {
            updateFileStore({
              key: objectKey,
              type: "progress",
              fileUploadProgress: 0,
            });
          }
        },
      },
    });

    try {
      return {
        fileKey: objectKey,
        item: result.result
          .then((data) => {
            updateFileStore({
              key: objectKey,
              type: "finish",
              fileUploadData: data,
            });
          })
          .catch((e) => {
            updateFileStore({
              key: objectKey,
              type: "error",
              fileUploadError: e,
            });
          }),
      };
    } catch (e) {
      throw new Error(`Failed to upload file: ${e}`);
    }
  }, []);

  return {
    uploadFile,
    fileStore,
    resetFileStore: () => {
      updateFileStore({ type: "clear" });
    },
    restoreFileStore: (fileStore: FileUploadStore) => {
      updateFileStore({ fileStore, type: "restore" });
    },
    removeFile: (key: string) => {
      updateFileStore({ key, type: "remove" });
      // TODO: Remove file from S3?
      // May have unintended consequences if removing a file that is
      // used in a separate chat message.
    },
  };
};

export const generateFileKey = ({
  providerId,
  patientId,
  fileName,
}: {
  providerId: string;
  patientId: string;
  fileName: string;
}) => {
  return `providers/${providerId}/${patientId}/${uuidv4()}/${truncateFileName(
    fileName,
  )}`;
};

const truncateFileName = (fileName: string) => {
  const charactersToCut = fileName.length - MAX_FILENAME_LENGTH;

  if (charactersToCut <= 0) {
    return fileName;
  }

  // preserve the file extension
  const fileNameParts = fileName.split(".");

  const extension = fileNameParts.pop();
  const baseName = fileNameParts.join(".");

  // If baseName === "", that means there was no extension
  if (baseName === "") {
    return fileName.slice(0, baseName.length - charactersToCut);
  }

  const truncatedFileName = baseName.slice(
    0,
    baseName.length - charactersToCut,
  );

  const cleanedExtension =
    extension && extension?.length > MAX_EXTENSION_LENGTH
      ? `_${extension.slice(0, MAX_EXTENSION_LENGTH)}`
      : `.${extension}`;

  return `${truncatedFileName}${cleanedExtension}`;
};

export const useChatFileUpload = () => {
  const { cognitoId: userId } = useAuth();

  const useFileUploadValue = useFileUpload();
  const uploadChatFileAsPatient = useCallback(
    async (file: File, providerId: string) => {
      if (!userId) {
        throw new Error("User is not logged in");
      }

      if (
        estimateAttachmentAttributeSize(
          // Estimate the size of the existing files plus the new file.
          Object.values(useFileUploadValue.fileStore)
            .map((f) => f.fileName)
            .concat([file.name]),
        ) >
        CHIME_MAX_ATTRIBUTE_SIZE - 100 // 100-char size buffer for space
      ) {
        toast.error(
          "You have reached the attachment limit. Please send your remaining files in a separate message.",
          {
            duration: 5000,
          },
        );
        return;
      }

      await useFileUploadValue.uploadFile(
        file,
        generateFileKey({
          providerId,
          patientId: userId,
          fileName: file.name,
        }),
      );

      if (
        file.name.endsWith(".jpg") ||
        file.name.endsWith(".jpeg") ||
        file.name.endsWith(".png")
      ) {
        fireClientPortalMixpanelEvent("photo_uploaded", {
          Page: "Chat",
        });
      } else {
        fireClientPortalMixpanelEvent("file_uploaded", {
          Page: "Chat",
        });
      }
    },
    [useFileUploadValue, userId],
  );

  const uploadChatFileAsProvider = useCallback(
    async (file: File, patientId: string) => {
      if (!userId) {
        throw new Error("User is not logged in");
      }
      await useFileUploadValue.uploadFile(
        file,
        generateFileKey({
          providerId: userId,
          patientId,
          fileName: file.name,
        }),
      );
    },
    [useFileUploadValue, userId],
  );

  return {
    fileStore: useFileUploadValue.fileStore,
    clearFileStore: useFileUploadValue.resetFileStore,
    restoreFileStore: useFileUploadValue.restoreFileStore,
    removeFile: useFileUploadValue.removeFile,
    uploadChatFileAsPatient,
    uploadChatFileAsProvider,
  };
};

const generateJournalEntryFileKey = (
  patientCognitoId: string,
  fileName: string,
): string => {
  // Extract extension from file name and truncate it to 10 characters just in case.
  const extension = fileName.split(".").pop()?.slice(0, 10) ?? "";

  return `journal-uploads/${patientCognitoId}/${uuidv4()}.${extension}`;
};

export const useJournalEntryPhotoUpload = (maxFiles: number = 10) => {
  const { cognitoId: userId } = useAuth();

  const useFileUploadValue = useFileUpload();

  const uploadJournalEntryFile = useCallback(
    async (file: File) => {
      if (!userId) {
        throw new Error("User is not logged in");
      }

      if (Object.keys(useFileUploadValue.fileStore).length >= maxFiles) {
        toast.error(
          `You can only upload ${maxFiles} photo${maxFiles !== 1 ? "s" : ""} per entry`,
        );
        return false;
      }

      const fileKey = generateJournalEntryFileKey(userId, file.name);

      await useFileUploadValue.uploadFile(file, fileKey);

      return true;
    },
    [useFileUploadValue, userId, maxFiles],
  );

  return {
    fileStore: useFileUploadValue.fileStore,
    clearFileStore: useFileUploadValue.resetFileStore,
    removeFile: useFileUploadValue.removeFile,
    uploadJournalEntryFile,
  };
};

const generateLabFileKey = ({
  providerId,
  patientId,
  fileName,
}: {
  providerId: string;
  patientId: string;
  fileName: string;
}) => {
  return `providers/${providerId}/${patientId}/${uuidv4()}/labs/${truncateFileName(
    fileName,
  )}`;
};

const useRespondentCognitoId = (): string | undefined => {
  const { useApiChat } = useChatImplementation();
  const { selectedChatChannel: apiSelectedConversation } =
    useSelectedChatChannel();
  const { selectedConversation: chimeSelectedConversation } = useChat();

  return useMemo(() => {
    if (useApiChat) {
      return apiSelectedConversation?.respondent.cognito_id;
    }

    return chimeSelectedConversation?.respondent.cognitoId;
  }, [useApiChat, apiSelectedConversation, chimeSelectedConversation]);
};

export const usePatientLabFileUpload = () => {
  const { cognitoId: userId } = useAuth();

  const respondentCognitoId = useRespondentCognitoId();
  const { isProvider } = useIdentity();

  const useFileUploadValue = useFileUpload();

  const uploadLabFile = useCallback(
    async (file: File) => {
      if (isProvider) {
        throw new Error("User is not a patient");
      }

      if (!userId) {
        throw new Error("User is not logged in");
      }

      if (!respondentCognitoId) {
        throw new Error("Provider id could not be found");
      }

      const fileKey = generateLabFileKey({
        providerId: respondentCognitoId,
        patientId: userId,
        fileName: file.name,
      });

      await useFileUploadValue.uploadFile(file, fileKey);
    },
    [useFileUploadValue, userId, isProvider, respondentCognitoId],
  );

  return {
    fileStore: useFileUploadValue.fileStore,
    clearFileStore: useFileUploadValue.resetFileStore,
    removeFile: useFileUploadValue.removeFile,
    uploadLabFile,
  };
};
